import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { brand } from "@templates/shared-theme/themePrimitives"
import { useParams } from 'react-router-dom';
import { useChallengeLeaderboardQuery, useChallengeWinnersAndContestantsCountQuery, useChallengeQuery } from '@query/challengeQuery';
import { useTranslation } from "react-i18next";
import ImageView from '@component/ImageView'; // Adjust the path as needed
import ImageAvatarView from '@component/ImageAvatarView';

import PlainImageViewer from '@component/PlainImageViewer';


/*--- COLORS --- */
const winnerPrimaryColor = brand[800];

/*---OTHER VARS--- */

const firstNamePlaceholder = "Anon"
const lastNamePlaceholder = "Anonymous"
const initialsPlaceholder = "AA"

const LeaderboardItem = ({ user, position }) => {
  const userPerformanceString = user.total_minutes === null ? `${user.workout_count}x` : `${(user.total_minutes / 60).toFixed(1)}h`
  let initials;
  if (user.first_name && user.last_name) {
    initials = `${user.first_name[0]}${user.last_name[0]}`;
  } else {
    initials = initialsPlaceholder;
  }

  const theme = useTheme();


  return (
    <div
      id="challenge-leaderboard-item"
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '1em 1.25em',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '0.75em',
        marginBottom: '0.5em',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        transition: 'all 0.2s ease'
      }}
    >
      {/* Position */}
      <div style={{
        width: '2em',
        height: '2em',
        borderRadius: '50%',
        backgroundColor: position === 1 ? winnerPrimaryColor : 'rgba(255, 255, 255, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: '0.75em',
        fontSize: '0.875em',
        fontWeight: '600',
        color: position === 1 ? '#000' : '#fff'
      }}>
        {position}
      </div>

      {/* Avatar */}
      {user.file_image_uuid ? (
        <PlainImageViewer
          imageStyle={{
            marginRight: '0.75em',
            width: '4.125em',
            height: '4.125em',
            backgroundColor: '#444'
          }}
          fileUuid={user.file_image_uuid} />
      ) : (
        <div
          style={{
            width: '3.125em',
            height: '3.125em',
            borderRadius: '50%',
            backgroundColor: '#444',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '0.75em',
            fontSize: '0.875em',
            fontWeight: '600',
            color: '#fff'
          }}
        >
          {initials.toUpperCase()}
        </div>
      )}

      {/* Name */}
      <div style={{ flex: 1 }}>
        <div style={{
          color: '#fff',
          fontSize: '1em',
          fontWeight: '500',
          marginBottom: '0.125em'
        }}>
          {user.first_name || firstNamePlaceholder} {user.last_name || lastNamePlaceholder}
        </div>
        {position === 1 && (
          <div style={{
            color: '#888',
            fontSize: '0.75em'
          }}>
            Current leader
          </div>
        )}
      </div>

      {/* Time */}
      <div style={{
        color: '#fff',
        fontSize: '1em',
        fontWeight: '500'
      }}>
        {userPerformanceString}
      </div>
    </div>
  );
};

const LoadingSkeleton = () => (
  <div style={{ marginBottom: '0.5em' }}>
    <div
      style={{
        height: '4.5em',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '0.75em',
        animation: 'pulse 1.5s ease-in-out infinite'
      }}
    />
  </div>
);

const SettingsPanel = ({ scale, setScale, isVisible, onClose }) => {
  if (!isVisible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      fontSize: '16px' // Reset to base font size for settings panel
    }}>
      <div style={{
        backgroundColor: '#1a1a1a',
        padding: '32px',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        minWidth: '400px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <h2 style={{
            color: '#fff',
            fontSize: '24px',
            fontWeight: '600',
            margin: 0
          }}>
            Display Settings
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#888',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            ×
          </button>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{
            color: '#fff',
            fontSize: '16px',
            fontWeight: '500',
            display: 'block',
            marginBottom: '12px'
          }}>
            Scale Factor: {scale.toFixed(1)}x
          </label>
          <input
            type="range"
            min="0.5"
            max="3.0"
            step="0.1"
            value={scale}
            onChange={(e) => setScale(parseFloat(e.target.value))}
            style={{
              width: '100%',
              height: '4px',
              borderRadius: '2px',
              background: '#333',
              outline: 'none',
              cursor: 'pointer'
            }}
            className="scale-slider"
          />
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            color: '#888',
            fontSize: '12px',
            marginTop: '8px'
          }}>
            <span>0.5x (Small)</span>
            <span>1.0x (Normal)</span>
            <span>3.0x (Large)</span>
          </div>
        </div>

        <div style={{
          padding: '16px',
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '8px',
          marginBottom: '24px'
        }}>
          <div style={{
            color: '#fff',
            fontSize: '14px',
            marginBottom: '8px'
          }}>
            <strong>Quick Presets:</strong>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {[
              { label: 'Small TV', value: 0.7 },
              { label: 'Medium TV', value: 1.0 },
              { label: 'Large TV', value: 1.4 },
              { label: 'Extra Large TV', value: 2.0 }
            ].map((preset) => (
              <button
                key={preset.label}
                onClick={() => setScale(preset.value)}
                style={{
                  backgroundColor: scale === preset.value ? winnerPrimaryColor : 'rgba(255, 255, 255, 0.1)',
                  color: scale === preset.value ? '#000' : '#fff',
                  border: 'none',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{
          color: '#888',
          fontSize: '12px',
          lineHeight: '1.4'
        }}>
          <strong>Instructions:</strong><br />
          • Adjust the scale to fit your TV screen size<br />
        </div>
      </div>
    </div>
  );
};

const LeftSidePanel = () => {
  return (
    <div style={{
      position: "fixed", // Changed from "absolute"
      bottom: "20px",    // Stick to the bottom
      left: "20px",      // Stick to the left
      width: '10%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'start',
      gap: '1.5em',
      zIndex: 1000        // Optional: ensure it stays above other content
    }}>
      <img src="/img/qr-code.png" style={{ width: '100%' }} alt="Smart link QR" />
      {/* <img src="/img/app_store_qr.png" style={{ width: '100%' }} alt="App Store QR" /> */}
    </div>
  );
};

const RightSidePanel = () => {
  return (
    <div style={{
      position: "fixed", // Changed from "absolute"
      bottom: "20px",    // Stick to the bottom
      right: "20px",      // Stick to the left
      width: '8%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'start',
      gap: '1.5em',
      zIndex: 1000        // Optional: ensure it stays above other content
    }}>
      <img src="/img/logo_text_512.png" style={{ width: '100%' }} alt="Logo" />
    </div>
  );
};

const ChallengeLeaderboardPage = () => {
  const [leaderboard, setLeaderboard] = useState();
  const [winnersInfo, setWinnersInfo] = useState();
  const [challengeInfo, setChallengeInfo] = useState();

  const [page, setPage] = useState(0);
  const { challengeUuid } = useParams();
  const [scale, setScale] = useState(() => {
    const saved = localStorage.getItem('leaderboard-scale');
    return saved ? parseFloat(saved) : 1.0;
  });

  const [showSettings, setShowSettings] = useState(false);
  const challengeLeaderboardQuery = useChallengeLeaderboardQuery(challengeUuid, page);
  const challengeWinnersAndContestantsCountQuery = useChallengeWinnersAndContestantsCountQuery(challengeUuid);
  const challengeQuery = useChallengeQuery(challengeUuid);
  const { t } = useTranslation(["challengeLeaderboard"]);

  const MAX_LEADERS_SHOWN = 3;

const getInitials = (first, last) => {
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  return initialsPlaceholder;
};
  
  useEffect(() => {
    localStorage.setItem('leaderboard-scale', scale.toString());
  }, [scale]);

  useEffect(() => {
    if (challengeLeaderboardQuery.data?.data) {
      setLeaderboard(challengeLeaderboardQuery.data.data);
    }
    if (challengeWinnersAndContestantsCountQuery.data?.data) {
      setWinnersInfo(challengeWinnersAndContestantsCountQuery.data.data);
    }
    if (challengeQuery.data?.data) {
      setChallengeInfo(challengeQuery.data.data);
    }
  }, [
    challengeLeaderboardQuery.data,
    challengeWinnersAndContestantsCountQuery.data,
    challengeQuery.data
  ]);

  return (
    <div id="challenge-leaderboard" style={{
      position: "relative"
    }}>
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 0.4;
          }
          50% {
            opacity: 0.6;
          }
        }
        
        .scale-slider::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: ${winnerPrimaryColor};
          cursor: pointer;
        }
        
        .scale-slider::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: ${winnerPrimaryColor};
          cursor: pointer;
          border: none;
        }
      `}</style>
      <LeftSidePanel />
      <RightSidePanel />
      <div
        style={{
          backgroundColor: '#0A0A0A',
          minHeight: '100vh',
          padding: '1.25em',
          fontSize: `${16 * scale}px` // Scale the root font size
        }}
      >
        {/* Settings Button - Fixed size, not scaled */}
        <button
          onClick={() => setShowSettings(true)}
          style={{
            position: 'fixed',
            top: '15px',
            right: '15px',
            width: '25px',
            height: '25px',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '8px',
            cursor: 'pointer',
            zIndex: 999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '14px',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
          }}
          title="Display Settings"
        >
          ⚙️
        </button>

        <div className="container" style={{
          margin: "0 auto",
          maxWidth: "75%",
          minWidth: "50%",
          width: `${scale * 60}%`
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '2em'
          }}>
            <h1 style={{
              color: '#fff',
              fontSize: '2em',
              fontWeight: '600',
              margin: 0
            }}>
              {challengeInfo?.name.toUpperCase() || ""}
            </h1>
          </div>

{/* Current Leader Circle */}
{(() => {
  const winners = winnersInfo?.winners ?? [];
  const leaders = winners.slice(0, MAX_LEADERS_SHOWN);
  const restCount = Math.max(0, winners.length - MAX_LEADERS_SHOWN);

  // Optional: cap visible dots so it doesn't create a long row
  const MAX_DOTS = 0;
  const dotsToShow = Math.min(restCount, MAX_DOTS);
  const remainingAfterDots = restCount - dotsToShow;

  return (
    <div style={{ display: "flex", flexDirection: "row", justifyContent: "center", gap: "1em", alignItems: "center" }}>
      {/* Leaders (max 3) */}
      {leaders.map((winner, index) => (
        <div
          key={winner.account_uuid || winner.uuid || index}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginBottom: "2em",
          }}
        >
          <div style={{ position: "relative", marginBottom: "1em" }}>
            {winner.file_image_uuid ? (
              <PlainImageViewer
                imageStyle={{
                  width: "5em",
                  height: "5em",
                }}
                fileUuid={winner.file_image_uuid}
              />
            ) : (
              <div
                style={{
                  width: "5em",
                  height: "5em",
                  borderRadius: "50%",
                  backgroundColor: "#444",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.5em",
                  fontWeight: "600",
                  color: "#fff",
                }}
              >
                {getInitials(winner.first_name, winner.last_name)}
              </div>
            )}

            {/* Badge: 1,2,3 */}
            <div
              style={{
                position: "absolute",
                top: "-0.25em",
                right: "-0.25em",
                width: "2em",
                height: "2em",
                borderRadius: "50%",
                backgroundColor: winnerPrimaryColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1em",
                fontWeight: "600",
                color: "#000",
              }}
            >
              {index + 1}
            </div>
          </div>

          <div
            style={{
              color: "#fff",
              fontSize: "1.125em",
              fontWeight: "600",
              marginBottom: "0.25em",
            }}
          >
            {`${winner.first_name || firstNamePlaceholder} ${winner.last_name || lastNamePlaceholder}`}
          </div>
          <div style={{ color: "#888", fontSize: "0.75em" }}>
            Current leader
          </div>
        </div>
      ))}

      {/* Dots for the rest */}
      {restCount > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.4em",
            marginBottom: "2em",
            padding: "0 0.5em",
            height: "100%"
          }}
          title={`${restCount} more winners`}
        >
          {Array.from({ length: dotsToShow }).map((_, i) => (
            <div
              key={i}
              style={{
                width: "0.55em",
                height: "0.55em",
                borderRadius: "50%",
                backgroundColor: "rgba(255, 255, 255, 0.35)",
              }}
            />
          ))}

          {/* If too many to show as dots, show +N */}
          {remainingAfterDots > 0 && (
            <div
              style={{
                marginLeft: "0.25em",
                color: "rgba(255,255,255,0.6)",
                fontSize: "0.9em",
                fontWeight: 600,
              }}
            >
              +{remainingAfterDots}
            </div>
          )}
        </div>
      )}
    </div>
  );
})()}
          {/* Leaderboard List */}
          <div>
            {challengeLeaderboardQuery.isLoading ? (
              Array.from({ length: 20 }).map((_, idx) => (
                <LoadingSkeleton key={idx} />
              ))
            ) : (
              leaderboard?.items.map((user, idx) => (
                <LeaderboardItem
                  key={user.uuid}
                  user={user}
                  position={user.position}
                />
              ))
            )}
          </div>
        </div>
      </div>

      <SettingsPanel
        scale={scale}
        setScale={setScale}
        isVisible={showSettings}
        onClose={() => setShowSettings(false)}
      />
    </div>
  );
};

export default ChallengeLeaderboardPage;